import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, ViewChild, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Transition } from '@uirouter/core';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';
import { UnsavedFormCheckService } from '@client/app/shared-modules/navigation-guard/unsaved-form-check.service';
import { JiraConnectorRepo } from '@client/app/feature-modules/shared/jira-connector/services/jira-connector.repo';
import { JiraConnectorSettings, JiraTestTicketResponse } from '@client/app/feature-modules/shared/jira-connector/types/jira-connector.type';
import { Tooltip } from 'primeng/tooltip';
import { LoginService } from '@client/app/core/services/login/login.service';
import { GlobalToastMessageService } from '@client/app/shared-modules/global-toast/services/global-toast-message.service';

type ApiFieldError = {
    field: string;
    message: string;
};
type ApiError = {
    status?: number;
    error?: {
        errors?: ApiFieldError[];
        message?: string;
    };
};

type ValidationError = {
    message: 'Validation error';
    errors: Record<string, string[]>;
};

@Component({
    selector: 'mon-admin-jira-connector',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TranslocoModule,
        CardModule,
        InputTextModule,
        ButtonModule,
        ProgressSpinnerModule,
        ConfirmDialog,
        PageLayoutComponent,
        Tooltip,
        MessageModule,
    ],
    providers: [ConfirmationService, UnsavedFormCheckService],
    templateUrl: './admin-jira-connector.component.html',
    styleUrl: './admin-jira-connector.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminJiraConnectorComponent implements OnInit {
    @ViewChild('form') form!: NgForm;
    @ViewChild(PageLayoutComponent) pageLayout?: PageLayoutComponent;

    loadingForm = signal(false);
    savingForm = signal(false);
    testingConnection = signal(false);
    deletingSettings = signal(false);
    settingsExist = signal(false);
    showForm = signal(false);
    apiTokenTouched = signal(false);
    testResponse = signal<JiraTestTicketResponse | null>(null);
    fieldErrors = signal<Record<string, string>>({});
    formErrorState = signal<{
        show: boolean;
        generalMessage: string;
        fieldErrorMessages: string[];
    } | null>(null);

    mode = computed(() => {
        if (this.settingsExist()) {
            return 'edit';
        }
        return 'create';
    });

    customFieldsArray: { key: string; value: string }[] = [{ key: '', value: '' }];

    settings: JiraConnectorSettings = {
        base_url: '',
        username: '',
        api_token: '',
        project_key: '',
        issue_type: '',
    };

    deregisterTransitionHook: (() => void) | null = null;

    constructor (
        private jiraConnectorRepo: JiraConnectorRepo,
        private translocoService: TranslocoService,
        private transition: Transition,
        private confirmationService: ConfirmationService,
        private unsavedFormCheckService: UnsavedFormCheckService,
        private destroyRef: DestroyRef,
        private loginService: LoginService,
        private globalToastMessageService: GlobalToastMessageService,
    ) {
        this.destroyRef.onDestroy(() => {
            this.unregisterUnsavedFormGuard();
        });
    }

    ngOnInit (): void {
        this.loadSettings();
    }

    private registerUnsavedFormGuard (): void {
        if (this.deregisterTransitionHook) {
            return;
        }
        this.deregisterTransitionHook = this.unsavedFormCheckService.registerGuard(
            this.transition.router.transitionService,
            () => this.hasUnsavedChanges(),
        );
    }

    private unregisterUnsavedFormGuard (): void {
        if (this.deregisterTransitionHook) {
            this.deregisterTransitionHook();
            this.deregisterTransitionHook = null;
        }
    }

    private hasUnsavedChanges (): boolean {
        if (!this.form || this.savingForm()) {
            return false;
        }
        return this.form.dirty === true;
    }

    private async loadSettings (): Promise<void> {
        this.loadingForm.set(true);
        try {
            this.settings = await this.jiraConnectorRepo.getSettings();
            this.settingsExist.set(true);
            this.showForm.set(true);

            if (this.settings.masked_api_token) {
                this.settings.api_token = this.settings.masked_api_token;
            }

            if (this.settings.custom_fields && Object.keys(this.settings.custom_fields).length > 0) {
                this.customFieldsArray = Object.entries(this.settings.custom_fields).map(([key, value]) => ({
                    key,
                    value,
                }));
            } else {
                this.customFieldsArray = [{ key: '', value: '' }];
            }

            this.registerUnsavedFormGuard();
        } catch (error: unknown) {
            // If settings don't exist (404), that's ok - user will create new settings
            if (this.isApiError(error) && error?.status !== 404) {
                this.globalToastMessageService.error(
                    this.translocoService.translate('admin.jiraConnector.toast.loadError'),
                );
            }
            this.settingsExist.set(false);
            this.showForm.set(false);
        } finally {
            this.loadingForm.set(false);
        }
    }

    onSetupClick (): void {
        this.showForm.set(true);
        this.customFieldsArray = [{ key: '', value: '' }];
        this.registerUnsavedFormGuard();
    }

    onApiTokenChange (): void {
        this.apiTokenTouched.set(true);
        this.clearTestResponse();
    }

    onFormFieldChange (): void {
        this.clearTestResponse();
    }

    private clearTestResponse (): void {
        this.testResponse.set(null);
    }

    private clearFieldErrors (): void {
        this.fieldErrors.set({});
        if (this.form?.form) {
            Object.keys(this.form.form.controls).forEach(key => {
                const control = this.form.form.get(key);
                if (control?.errors?.['serverError']) {
                    const errors = { ...control.errors };
                    delete errors['serverError'];
                    control.setErrors(Object.keys(errors).length ? errors : null);
                }
            });
        }
    }

    private mapErrorFieldToControlNames (errorField: string): string[] {
        // Handle multi-field authentication error
        if (errorField === 'base_url, username, api_token') {
            return ['baseUrl', 'username', 'apiToken'];
        }
        // Handle single field errors
        const fieldMap: Record<string, string> = {
            'base_url': 'baseUrl',
            'username': 'username',
            'api_token': 'apiToken',
            'project_key': 'projectKey',
            'issue_type': 'issueType',
        };
        if (fieldMap[errorField]) {
            return [fieldMap[errorField]];
        }
        // Handle custom field errors - check if it matches any custom field key
        const customFieldIndex = this.customFieldsArray.findIndex(
            field => field.key.trim() === errorField,
        );
        if (customFieldIndex !== -1) {
            return [`customFieldKey${customFieldIndex}`, `customFieldValue${customFieldIndex}`];
        }
        return [errorField];
    }

    private isApiTokenInvalid (): boolean {
        if (this.mode() === 'edit' && this.apiTokenTouched() && !this.settings.api_token?.trim()) {
            return true;
        }
        return false;
    }

    async onSubmit (form: NgForm): Promise<void> {
        if (form.invalid || this.isApiTokenInvalid()) {
            return;
        }

        const customFieldsRecord: Record<string, string> = {};
        for (const field of this.customFieldsArray) {
            const trimmedKey = field.key.trim();
            if (trimmedKey && !customFieldsRecord[trimmedKey]) {
                customFieldsRecord[trimmedKey] = field.value;
            }
        }

        const payload: Partial<JiraConnectorSettings> = {
            base_url: this.settings.base_url,
            username: this.settings.username,
            project_key: this.settings.project_key,
            issue_type: this.settings.issue_type,
        };

        payload.custom_fields = customFieldsRecord;

        // Include api_token only when creating new settings or when it's been modified
        if (this.mode() === 'create' || this.apiTokenTouched()) {
            payload.api_token = this.settings.api_token;
        }

        this.savingForm.set(true);
        try {
            if (this.mode() === 'edit') {
                this.settings = await this.jiraConnectorRepo.updateSettings(payload);
            } else {
                this.settings = await this.jiraConnectorRepo.createSettings(payload as JiraConnectorSettings);
                this.settingsExist.set(true);
            }

            if (this.settings.masked_api_token) {
                this.settings.api_token = this.settings.masked_api_token;
            }
            this.apiTokenTouched.set(false);
            this.clearFieldErrors();
            this.clearFormError();
            form.form.markAsPristine();

            await this.loginService.loadUser();

            this.globalToastMessageService.success(
                this.translocoService.translate('admin.jiraConnector.toast.saveSuccess'),
            );
        } catch (error: unknown) {
            if (this.isValidationError(error)) {
                this.handleValidationErrors(error.errors);
                return;
            }

            if (this.isApiError(error)) {
                const generalMessage = this.translocoService.translate('admin.jiraConnector.toast.saveError');
                if (error?.error?.errors && Array.isArray(error.error.errors)) {
                    this.setFormError(generalMessage, error.error.errors);
                    this.handleFieldErrors(error.error.errors);
                } else if (error?.error?.message) {
                    this.setFormError(error.error.message);
                } else {
                    this.setFormError(generalMessage);
                }
            } else {
                this.setFormError(this.translocoService.translate('admin.jiraConnector.toast.genericError'));
            }
        } finally {
            this.savingForm.set(false);
            this.pageLayout?.scrollToTop();
        }
    }

    async onTest (): Promise<void> {
        this.testingConnection.set(true);
        this.clearFieldErrors();
        try {
            const response = await this.jiraConnectorRepo.testTicket();
            this.testResponse.set(response);

            if (response.success) {
                this.clearFormError();
                this.globalToastMessageService.success(
                    this.translocoService.translate('admin.jiraConnector.toast.testSuccess'),
                );
            } else {
                if (response.errors && response.errors.length > 0) {
                    const translatedErrors = response.errors.map(error => {
                        if (error.field === 'base_url, username, api_token' &&
                            error.message === 'are invalid, cannot authenticate') {
                            return {
                                field: error.field,
                                message: this.translocoService.translate('admin.jiraConnector.toast.authenticationError'),
                            };
                        }
                        if (error.field === 'project_key') {
                            return {
                                field: error.field,
                                message: this.translocoService.translate('admin.jiraConnector.toast.invalidProjectKeyError'),
                            };
                        }
                        if (error.field === 'issue_type') {
                            return {
                                field: error.field,
                                message: this.translocoService.translate('admin.jiraConnector.toast.invalidIssueTypeError'),
                            };
                        }
                        return error;
                    });

                    this.handleFieldErrors(translatedErrors);
                    this.setFormError(
                        this.translocoService.translate('admin.jiraConnector.toast.testError'),
                        translatedErrors,
                    );
                } else {
                    this.setFormError(
                        response.message || this.translocoService.translate('admin.jiraConnector.toast.testError'),
                    );
                }
            }
        } catch (error: unknown) {
            this.testResponse.set(null);
            if (this.isApiError(error)) {
                const generalMessage = this.translocoService.translate('admin.jiraConnector.toast.testError');
                if (error?.error?.errors && Array.isArray(error.error.errors)) {
                    this.setFormError(generalMessage, error.error.errors);
                    this.handleFieldErrors(error.error.errors);
                } else if (error?.error?.message) {
                    this.setFormError(error.error.message);
                } else {
                    this.setFormError(generalMessage);
                }
            } else {
                this.setFormError(this.translocoService.translate('admin.jiraConnector.toast.genericError'));
            }
        } finally {
            this.testingConnection.set(false);
            this.pageLayout?.scrollToTop();
        }
    }

    async onDelete (): Promise<void> {
        this.confirmationService.confirm({
            header: this.translocoService.translate('admin.jiraConnector.deleteDialog.header'),
            message: this.translocoService.translate('admin.jiraConnector.deleteDialog.message'),
            closable: true,
            closeOnEscape: true,
            dismissableMask: true,
            rejectButtonProps: {
                label: this.translocoService.translate('admin.jiraConnector.deleteDialog.cancelButtonLabel'),
                severity: 'secondary',
                variant: 'text',
            },
            acceptButtonProps: {
                label: this.translocoService.translate('admin.jiraConnector.deleteDialog.deleteButtonLabel'),
                severity: 'danger',
            },
            accept: async () => {
                this.deletingSettings.set(true);
                try {
                    await this.jiraConnectorRepo.deleteSettings();

                    this.settings = {
                        base_url: '',
                        username: '',
                        api_token: '',
                        project_key: '',
                        issue_type: '',
                    };
                    this.customFieldsArray = [{ key: '', value: '' }];
                    this.settingsExist.set(false);
                    this.showForm.set(false);
                    this.apiTokenTouched.set(false);
                    this.testResponse.set(null);
                    this.fieldErrors.set({});
                    this.unregisterUnsavedFormGuard();

                    await this.loginService.loadUser();

                    this.globalToastMessageService.success(
                        this.translocoService.translate('admin.jiraConnector.toast.deleteSuccess'),
                    );
                } catch (error: unknown) {
                    this.globalToastMessageService.error(
                        this.translocoService.translate('admin.jiraConnector.toast.genericError'),
                    );
                } finally {
                    this.deletingSettings.set(false);
                }
            },
        });
    }

    addCustomField (): void {
        this.customFieldsArray.push({ key: '', value: '' });
        if (this.form) {
            this.form.form.markAsDirty();
        }
        this.clearTestResponse();
    }

    removeCustomField (index: number): void {
        if (this.customFieldsArray.length === 1) {
            this.customFieldsArray[0] = { key: '', value: '' };
            if (this.form) {
                this.form.form.markAsDirty();
            }
            return;
        }

        if (this.customFieldsArray.length > 1) {
            this.customFieldsArray.splice(index, 1);
            if (this.form) {
                this.form.form.markAsDirty();
            }
            this.clearTestResponse();
        }
    }

    hasDuplicateKey (index: number): boolean {
        const currentKey = this.customFieldsArray[index].key.trim().toLowerCase();
        if (!currentKey) {
            return false;
        }
        return this.customFieldsArray.some(
            (field, i) => i !== index && field.key.trim().toLowerCase() === currentKey,
        );
    }

    private setFormError (generalMessage: string, errors?: ApiFieldError[]): void {
        const fieldErrorMessages: string[] = [];

        if (errors && errors.length > 0) {
            const uniqueMessages = new Set<string>();
            for (const error of errors) {
                if (!uniqueMessages.has(error.message)) {
                    uniqueMessages.add(error.message);
                    if (this.isCustomFieldError(error.field)) {
                        fieldErrorMessages.push(`${error.field}: ${error.message}`);
                    }
                }
            }
        }

        this.formErrorState.set({
            show: true,
            generalMessage,
            fieldErrorMessages,
        });
    }

    private clearFormError (): void {
        this.formErrorState.set(null);
    }

    private handleFieldErrors (errors: ApiFieldError[]): void {
        const newFieldErrors: Record<string, string> = {};
        for (const error of errors) {
            const controlNames = this.mapErrorFieldToControlNames(error.field);
            for (const controlName of controlNames) {
                const control = this.form.form.get(controlName);
                if (control) {
                    control.setErrors({ serverError: true });
                    control.markAsTouched();
                    newFieldErrors[controlName] = error.message;
                }
            }
        }
        this.fieldErrors.set(newFieldErrors);
    }

    private handleValidationErrors (errors: Record<string, string[]>): void {
        const newFieldErrors: Record<string, string> = {};
        for (const errorField of Object.keys(errors)) {
            const controlNames = this.mapErrorFieldToControlNames(errorField);
            for (const controlName of controlNames) {
                const control = this.form.form.get(controlName);
                if (control) {
                    control.setErrors({ serverError: true });
                    control.markAsTouched();
                    newFieldErrors[controlName] = errors[errorField].join(', ');
                }
            }
        }
        this.fieldErrors.set(newFieldErrors);
    }

    private formatApiErrors (errors: ApiFieldError[]): string {
        return errors.map(error => error.message).join('\n');
    }

    private isApiError (error: unknown): error is ApiError {
        return (typeof error === 'object' && error !== null && ('status' in error || 'error' in error || 'errors' in error));
    }

    private isValidationError (error: unknown): error is ValidationError {
        return typeof error === 'object' && error !== null && ('message' in error) && error.message === 'Validation error';
    }

    private isCustomFieldError (field: string): boolean {
        return ![
            'base_url',
            'username',
            'api_token',
            'project_key',
            'issue_type',
        ].includes(field);
    }
}
